import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import { Checkbox } from './pilot-ui/checkbox';
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartData, ChartSeries } from '../types';
import NoteMarkerOverlay, { SeriesNotes } from './NoteMarkerOverlay';
import { ChartCopy, ChartUnavailable, explainEmptyAcross } from './chartUnavailable';
import { formatChartDayLabel } from '../utils/calendarDate';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { measureRange } from './PressureChart';
import { AXIS_TITLE_STYLE, X_TICK_MARGIN, inH2OTickFormatter, sharedPaDomain } from '../utils/pressureAxis';
import { DATA_LINE_Z_INDEX } from './referenceLineLayer';
import { buildDeltaRows, hasMeasurements, measuredDeltas } from './transitionDeltaSeries';
import TransitionDeltaTooltip from './TransitionDeltaTooltip';
import { renderDeltaMarkerLines } from './TransitionDeltaMarkerLines';

interface FanDeltasChartProps {
  jsonData: ChartData;
  yDomain?: [number, number];
  overlaySeries?: ChartSeries[];
  noteSeries?: SeriesNotes[];
  onNoteImageClick?: (noteId: number) => void;
}

const COPY: ChartCopy = {
  description:
    'This chart shows the fan-side pressure change when the blower switches on and off.',
  measurement: 'transition',
};

// Browser-remembered preference: anchor the delta axis at zero instead of
// letting recharts fit the data range.
const ZERO_PREF_KEY = 'fanDeltasChart.includeZero';

const FanDeltasChart: React.FC<FanDeltasChartProps> = ({
  jsonData,
  yDomain: propDomain,
  overlaySeries,
  noteSeries,
  onNoteImageClick,
}) => {
  const activeDot = useAnalysisActiveDot({ r: 8 });
  const [includeZero, setIncludeZero] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(ZERO_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleIncludeZero = () => {
    const next = !includeZero;
    setIncludeZero(next);
    try {
      localStorage.setItem(ZERO_PREF_KEY, next ? '1' : '0');
    } catch {
      // storage unavailable (private mode) — preference just won't persist
    }
  };
  const isOverlay = !!overlaySeries && overlaySeries.length > 1;
  const series: ChartSeries[] = isOverlay
    ? overlaySeries!
    : [{ id: 'solo', label: 'Pressure Transitions', color: '#ff9800', data: jsonData }];

  // Legacy bail-out, now only for payloads carrying neither values nor a
  // status block — otherwise the panel explains itself below.
  const anyPayload = series.some((s) => s.data?.fan_deltas || s.data?.fan_deltas_status);
  if (!anyPayload) return null;

  const seriesTransitions = series.map((s) => measuredDeltas(s.data, 'fan'));

  // Same gate as the return-side chart, so the same blank-panel problem: the
  // fan delta is computed in the identical guarded block in the pipeline. The
  // two are NOT interchangeable though — over 30 days, 147 day-rows carry a fan
  // delta with no return delta — so this reads its own status.
  if (seriesTransitions.every((ts) => !hasMeasurements(ts))) {
    const explanation = explainEmptyAcross(
      series.map((s) => ({ label: s.label, status: s.data?.fan_deltas_status })),
      COPY
    );
    if (explanation) {
      return (
        <div className="w-full mt-4">
          <h3 className="text-lg font-semibold mb-4">Fan Pressure Transition Deltas</h3>
          <ChartUnavailable {...explanation} />
        </div>
      );
    }
    return null;
  }

  const chartData = buildDeltaRows(series, 'fan');

  // An explicit yDomain prop (shared with the return-side chart so the two stay
  // comparable) still bounds the data; checking the box widens that window to
  // reach zero rather than discarding it. Deltas are signed, so extend toward
  // zero from whichever side the data sits on. Both axes take this one pair:
  // the inH₂O axis has no series of its own to fit (FS-709).
  const [dataMin, dataMax] = measureRange(chartData, /^delta_/);
  const yDomain = sharedPaDomain(
    Math.min(dataMin, propDomain?.[0] ?? Infinity),
    Math.max(dataMax, propDomain?.[1] ?? -Infinity),
    includeZero,
  );

  const csvColumns: CsvColumn<(typeof chartData)[number]>[] = [
    { header: 'Date', value: 'date' },
    ...series.map((s) => ({
      header: isOverlay ? `${s.label} — Pressure delta (Pa)` : 'Pressure delta (Pa)',
      value: `delta_${s.id}`,
    })),
  ];

  return (
    <div className="w-full mt-4">
      <div className="flex items-start justify-between gap-2 mb-8">
        <h3 className="text-lg font-semibold">Fan Pressure Transition Deltas</h3>
        <ChartCsvButton chart="fan-deltas" rows={chartData} columns={csvColumns} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 10, right: 50, left: 50, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" yAxisId="left" />
              <XAxis
                dataKey="date"
                tickFormatter={formatChartDayLabel}
                tickMargin={X_TICK_MARGIN}
                label={{ value: 'Date', position: 'insideBottom', offset: -20 }}
              />
              <YAxis
                yAxisId="left"
                domain={yDomain}
                allowDataOverflow
                tickFormatter={(v: number) => Math.round(v).toString()}
                label={{
                  value: 'Pressure Delta (Pa)',
                  angle: -90,
                  position: 'insideLeft',
                  style: AXIS_TITLE_STYLE,
                  offset: -35,
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={yDomain}
                allowDataOverflow
                tick={{ fontSize: 12 }}
                tickFormatter={inH2OTickFormatter(yDomain)}
                label={{
                  value: 'Pressure Delta (inH₂O)',
                  angle: 90,
                  position: 'insideRight',
                  style: AXIS_TITLE_STYLE,
                  offset: -35,
                }}
              />
              <Tooltip content={<TransitionDeltaTooltip series={series} isOverlay={isOverlay} />} />
              {isOverlay && <Legend verticalAlign="top" height={20} itemSorter={null} />}
              {series.map((s) => (
                <Line
                  key={`delta_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`delta_${s.id}`}
                  name={s.label}
                  yAxisId="left"
                  stroke={s.color}
                  dot={{ fill: s.color }}
                  activeDot={activeDot}
                />
              ))}
              {renderDeltaMarkerLines({ series, rows: chartData })}
              {noteSeries && noteSeries.length > 0 && onNoteImageClick && (
                <NoteMarkerOverlay seriesNotes={noteSeries} onImageClick={onNoteImageClick} />
              )}
            </LineChart>
          </ResponsiveContainer>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 select-none cursor-pointer w-fit">
            <Checkbox
              className="size-6"
              checked={includeZero}
              onCheckedChange={toggleIncludeZero}
              title="Include zero on the pressure axis"
            />
            Include zero on the pressure axis
          </label>
        </div>
      </div>
    </div>
  );
};

export default FanDeltasChart;
